import React from 'react';
import { motion } from 'framer-motion';
import { Award, Wrench, BookOpenCheck, Users, GraduationCap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { CERTIFICATIONS, SKILLS, EDUCATION } from '../mock/data';

const SkillGroup = ({ icon: Icon, title, items }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#C4A35A]" />
      <h4 className="font-heading text-lg text-[#F0EDE8]">{title}</h4>
    </div>
    <ul className="space-y-2">
      {items.map((s) => (
        <li key={s} className="flex items-start gap-2 text-[#DCCFAD] text-[14px]">
          <span className="mt-2 flex-shrink-0 inline-block w-[8px] h-[1px]" style={{ background: '#C4A35A' }} />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Credentials = () => {
  const { t } = useLanguage();

  return (
    <section id="credentials" className="section relative paper-texture">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="label-small mb-4">{t.credentials.label}</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
            {t.credentials.heading}
          </h2>
          <div className="gold-rule mt-6" />
        </motion.div>

        {/* Education */}
        <div className="mt-14" data-testid="education-section">
          <div className="flex items-center gap-2 mb-6">
            <GraduationCap size={16} className="text-[#C4A35A]" />
            <h3 className="font-heading text-2xl text-[#F0EDE8]">{t.credentials.education}</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {EDUCATION.map((e, i) => (
              <motion.div
                key={e.degree}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border border-[rgba(196,163,90,0.25)] bg-[rgba(196,163,90,0.03)] p-6"
                data-testid="education-card"
              >
                <div className="font-heading text-xl md:text-2xl text-[#F0EDE8] leading-tight">{e.degree}</div>
                <div className="font-heading italic text-[#C4A35A] mt-1">{e.school}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#8a7a54] mt-1">{e.location}</div>
                <ul className="mt-4 space-y-1.5">
                  {e.details.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-[#DCCFAD] text-[13px] leading-relaxed">
                      <span className="mt-2 flex-shrink-0 inline-block w-[8px] h-[1px]" style={{ background: '#C4A35A' }} />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Certifications */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-6">
              <Award size={16} className="text-[#C4A35A]" />
              <h3 className="font-heading text-2xl text-[#F0EDE8]">{t.credentials.trainings}</h3>
            </div>

            <div className="space-y-4">
              {CERTIFICATIONS.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="grid grid-cols-[80px_1fr] gap-4 border-l border-[#C4A35A] pl-5 py-2"
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C4A35A] font-heading pt-1">
                    {c.year}
                  </div>
                  <div>
                    <div className="font-heading text-lg text-[#F0EDE8] leading-tight">
                      {c.title}
                    </div>
                    <div className="text-[13px] text-[#8a7a54] mt-1">
                      {c.body}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-6">
              <Wrench size={16} className="text-[#C4A35A]" />
              <h3 className="font-heading text-2xl text-[#F0EDE8]">{t.credentials.skills}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <SkillGroup icon={BookOpenCheck} title={t.credentials.litigation} items={SKILLS.litigation} />
              <SkillGroup icon={BookOpenCheck} title={t.credentials.research} items={SKILLS.research} />
              <div className="sm:col-span-2">
                <SkillGroup icon={Users} title={t.credentials.clientAdvocacy} items={SKILLS.clientAdvocacy} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Credentials;
